import { memo } from 'react';
import { TFunction, useTranslation } from 'react-i18next';

import { IconSvg, ProgressBar, Text } from '@/lib/v2/components';
import { CloseIcon } from '@/lib/v2/icons/outline';
import { ArrowUpRight } from '@/lib/v2/icons/solid/ArrowUpRight';

import { quantifier } from '@/src/ContactsModule/utils/quantifier';
import { ContactDistributionProgressStatus, EnhancedContactDistributionReport } from '@/src/infrastructure/interfaces/IContactDistributionBulk';
import { loadingStatus } from '@/src/presentation/types/enum/importStatus.enum';
const getStatusColor = (status: ContactDistributionProgressStatus) => {
  switch (status) {
    case 'failed':
      return 'alert';
    case 'completed':
      return 'light-black';
    default:
      return 'primary';
  }
};

const getStatusMessage = (status: ContactDistributionProgressStatus, t: TFunction) => {
  switch (status) {
    case 'initializing':
      return t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.bulk.progress_notification.initializing');
    case 'creating_groups':
      return t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.bulk.progress_notification.creating_groups');
    case 'distributing':
      return t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.bulk.progress_notification.distributing');
    case 'completed':
      return t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.bulk.progress_notification.completed');
    case 'failed':
      return t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.bulk.progress_notification.failed');
    default:
      return t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.bulk.progress_notification.processing');
  }
};

interface ContactDistributionNotificationProps {
  activeProcesses: EnhancedContactDistributionReport[];
  onClose: (processId: string) => void;
  onGoTo: (processId: string) => void;
}

const loadingBarStatusMap = new Map<ContactDistributionProgressStatus, loadingStatus>([
  ['initializing', loadingStatus.loading],
  ['creating_groups', loadingStatus.loading],
  ['distributing', loadingStatus.loading],
  ['completed', loadingStatus.ready],
  ['failed', loadingStatus.error],
]);

const processHasFinished = (process: EnhancedContactDistributionReport) => {
  return process.status === 'completed' || process.status === 'failed';
};


const ContactDistributionNotification = ({ activeProcesses, onClose, onGoTo }: ContactDistributionNotificationProps) => {
  const { t } = useTranslation();

  if (activeProcesses.length === 0) return null;

  return (
    <div className="fixed bottom-4 left-4 z-50 flex flex-col gap-2">
      {activeProcesses.map((process) => (
        <div
          key={process.processId}
          className="w-80 rounded-lg bg-white p-4 shadow-lg"
        >
          <div className="mb-2 flex items-center justify-between">
            <Text color="light-black" variant="text-sm">
              {process.request.prefixGroupName}
            </Text>
            <div className="flex items-center gap-1">
              <Text
                color={getStatusColor(process.status)}
                variant="text-sm"
              >
                {getStatusMessage(process.status, t)}
              </Text>
              {processHasFinished(process) && (
                <>
                  <button type="button" onClick={() => onGoTo(process.processId)}>
                    <span className="sr-only">View created groups</span>
                    <IconSvg svgComponent={<ArrowUpRight color="#919296" height="10" width="10" />} />
                  </button>
                  <button type="button" onClick={() => onClose(process.processId)}>
                    <span className="sr-only">Close</span>
                    <IconSvg svgComponent={<CloseIcon color="#919296" height="10" width="10" />} />
                  </button>
                </>
              )}
            </div>
          </div>
          {process.progress && (
            <>
              <ProgressBar
                percentage={process.progressPercentage || 0}
                status={loadingBarStatusMap.get(process.status) || loadingStatus.loading}
              />
              <div className="flex flex-col gap-1 pt-2">
                <Text color="light-black" variant="text-sm">
                  {t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.bulk.progress_notification.processedBatches', {
                    processed: process.progress.processedBatches,
                    total: process.progress.totalBatches,
                  })}
                </Text>
                <Text color="light-black" variant="text-sm">
                  {t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.bulk.progress_notification.distributedContacts', {
                    distributed: quantifier(process.progress.totalContactsDistributed),
                    total: quantifier(process.progress.totalContactsToDistribute),
                  })}
                </Text>
                {process.estimatedTimeLeft !== undefined && (
                  <Text color="light-black" variant="text-sm">
                    {t('MANAGE_DISTRIBUTE_CONTACTS_MODAL.bulk.progress_notification.estimatedTimeLeft', {
                      time: process.estimatedTimeLeft,
                    })}s
                  </Text>
                )}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export default memo(ContactDistributionNotification);
